
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { updateCampPhoto, updatePhotoInLocationVersion } from "@/services/campPhotoService";
import { toast } from "sonner";
import { LocationPhoto } from "@/types/campPhoto";
import { useAuth } from "@/context/AuthContext";

const formSchema = z.object({
  photoReference: z.string().min(1, "Photo credit is required"),
  caption: z.string().max(100, "Caption must be 100 characters or less").optional(),
  altText: z.string().max(100, "Alt text must be 100 characters or less").optional(),
  yOffset: z.number().min(-1).max(1).default(0)
});

export type PhotoEditFormValues = z.infer<typeof formSchema>;

export const usePhotoEditForm = (
  locationId: string,
  photoIndex: number,
  initialData: LocationPhoto,
  onSuccess: () => void,
) => {
  const { refreshAuthToken, currentUser } = useAuth();
  
  console.log("usePhotoEditForm: Initializing with data:", {
    locationId,
    photoIndex,
    initialData: {
      ...initialData,
      yOffset: initialData.yOffset || 0,
      caption: initialData.caption || "",
      altText: initialData.altText || ""
    }
  });
  
  const form = useForm<PhotoEditFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      photoReference: initialData.photoReference || "",
      caption: initialData.caption || "",
      altText: initialData.altText || "",
      yOffset: initialData.yOffset || 0
    }
  });

  const handleSubmit = async (values: PhotoEditFormValues) => {
    console.log("usePhotoEditForm: handleSubmit called with values:", values);
    
    try {
      // Ensure auth token is refreshed before submission
      if (refreshAuthToken) {
        console.log("usePhotoEditForm: Refreshing auth token before submission");
        await refreshAuthToken();
        console.log("usePhotoEditForm: Auth token refreshed successfully");
      }
      
      // Update photo in location version
      console.log("usePhotoEditForm: Updating photo in location version", {
        locationId,
        photoIndex,
        values
      });
      
      // Ensure all values have defaults
      const updatedValues = {
        photoReference: values.photoReference || initialData.photoReference || "",
        caption: values.caption !== undefined ? values.caption : (initialData.caption || ""),
        altText: values.altText !== undefined ? values.altText : (initialData.altText || ""),
        yOffset: values.yOffset !== undefined ? values.yOffset : (initialData.yOffset || 0),
      };
      
      await updatePhotoInLocationVersion(
        locationId,
        currentUser?.uid || "system",
        photoIndex,
        updatedValues
      );

      // Location photo updated successfully; report success first
      console.log("usePhotoEditForm: Location photo updated successfully");
      toast.success("Photo updated successfully");
      onSuccess();

      // Try to update the underlying campPhoto document when applicable (non-placeholder)
      const campPhotoId = typeof initialData.campPhotoId === 'string' 
        ? initialData.campPhotoId 
        : initialData.campPhotoId?.id;

      const isPlaceholder = campPhotoId === '_placeholder' || campPhotoId === 'campPhotos/_placeholder';
      if (campPhotoId && !isPlaceholder) {
        try {
          console.log("usePhotoEditForm: Updating camp photo with ID:", campPhotoId);
          await updateCampPhoto(campPhotoId, updatedValues);
        } catch (e) {
          console.warn("usePhotoEditForm: campPhoto update failed (non-blocking)", e);
        }
      } else if (!campPhotoId) {
        console.warn("usePhotoEditForm: No campPhotoId found, skipping campPhoto update");
      } else {
        console.log("usePhotoEditForm: Placeholder photo detected; skipping campPhoto update");
      }
    } catch (error) {
      console.error("usePhotoEditForm: Error updating photo:", error);
      toast.error("Failed to update photo: " + (error instanceof Error ? error.message : String(error)));
      throw error; // Re-throw to allow the calling component to handle it
    }
  };

  const copyFromCaption = () => {
    const caption = form.getValues("caption");
    if (caption) {
      console.log("usePhotoEditForm: Copying caption to alt text:", caption);
      form.setValue("altText", caption);
    }
  };

  return {
    form,
    handleSubmit,
    copyFromCaption
  };
};
